"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { HiArrowLeft } from "react-icons/hi2";
import { RiCloseLine, RiLogoutBoxLine, RiMore2Fill, RiSearchLine } from "react-icons/ri";
import ProviderAvatar from "@/components/provider-avatar";
import { PresenceIndicator } from "@/components/team-chat/presence-indicator";
import Button from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { useUser } from "@/contexts/user-provider";
import { cx } from "@/lib/tailwind-utils";
import type { TeamConversationSummary } from "@/types/team-chat";
import { AddMemberPopover } from "./add-member-popover";
import { ConversationDetail } from "./conversation-detail";
import { ConversationList } from "./conversation-list";
import { SetStatusPopover } from "./set-status-popover";
import { useLeaveConversation, useOnlineStatusMaps, useTeamConversation, useUpdateConversation } from "@/query/team-chat";
import { useTeamChatDrawerStore } from "@/stores/team-chat-store";
function useConversationHeaderInfo(conversation: TeamConversationSummary | null | undefined, currentUserId: string | undefined) {
  if (!conversation) return {
    title: "",
    subtitle: "",
    isSelfDm: false
  };
  if (conversation.type === "DM") {
    // Self-DM: only member is the current user
    const isSelfDm = conversation.members.length === 1 && conversation.members[0].providerId === currentUserId;
    if (isSelfDm) {
      const self = conversation.members[0];
      return {
        title: `${self.firstName} ${self.lastName} (you)`,
        subtitle: "",
        isSelfDm: true
      };
    }
    const other = conversation.members.find(m => m.providerId !== currentUserId);
    return {
      title: other ? `${other.firstName} ${other.lastName}` : conversation.members.map(m => `${m.firstName} ${m.lastName}`).join(", "),
      subtitle: "",
      isSelfDm: false
    };
  }

  // Group
  const title = conversation.name || conversation.members.map(m => m.firstName).join(", ");
  return {
    title,
    subtitle: "",
    isSelfDm: false
  };
}
export function TeamChatPanel(t0) {
  const $ = _c(87);
  const {
    className,
    mode: t1,
    onClose
  } = t0;
  const mode = t1 === undefined ? "desktop" : t1;
  const {
    activeConversationId,
    setActiveConversation,
    closeDrawer
  } = useTeamChatDrawerStore();
  const {
    currentUser
  } = useUser();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState("");
  const nameInputRef = useRef(null);
  const leaveConversation = useLeaveConversation();
  const updateConversation = useUpdateConversation(activeConversationId ?? "");
  const {
    data: activeConversation
  } = useTeamConversation(activeConversationId);
  const {
    onlineStatusMap,
    presenceMap
  } = useOnlineStatusMaps();
  const {
    title: conversationTitle,
    isSelfDm
  } = useConversationHeaderInfo(activeConversation, currentUser?.id);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsSearchOpen(false);
      setIsEditingName(false);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== activeConversationId) {
    t3 = [activeConversationId];
    $[1] = activeConversationId;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(t2, t3);
  currentUser?.id;
  let t4;
  bb0: {
    if (!activeConversation || activeConversation.type !== "DM") {
      t4 = null;
      break bb0;
    }
    if (isSelfDm) {
      t4 = activeConversation.members[0] ?? null;
      break bb0;
    }
    let t5;
    if ($[3] !== activeConversation.members || $[4] !== currentUser?.id) {
      t5 = activeConversation.members.find(m => m.providerId !== currentUser?.id) ?? activeConversation.members[0];
      $[3] = activeConversation.members;
      $[4] = currentUser?.id;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    t4 = t5;
  }
  const dmAvatarMember = t4;
  const isMobile = mode === "mobile";
  const showMobileDetail = isMobile && activeConversationId;
  let t5;
  if ($[6] !== closeDrawer || $[7] !== onClose) {
    t5 = () => {
      if (onClose) {
        onClose();
      } else {
        closeDrawer();
      }
    };
    $[6] = closeDrawer;
    $[7] = onClose;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleClose = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h2 className="shrink-0 text-body-emphasis text-foreground">Whisper</h2>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== handleClose) {
    t7 = <div className="flex h-8 items-center gap-2">{t6}<div className="ml-auto flex shrink-0 items-center"><Button variant="ghost" className="size-7 !shadow-none text-foreground-muted hover:text-foreground" onClick={handleClose} icon={RiCloseLine} tooltip="Close Whisper" aria-label="Close Whisper" /></div></div>;
    $[10] = handleClose;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center self-start"><SetStatusPopover /></div>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7) {
    t9 = <div className="flex shrink-0 flex-col border-b border-border px-3 py-2">{t7}{t8}</div>;
    $[13] = t7;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const listHeader = t9;
  let t10;
  if ($[15] !== isMobile || $[16] !== setActiveConversation) {
    t10 = isMobile && <button onClick={() => setActiveConversation(null)} className="shrink-0 rounded p-1.5 text-foreground-muted transition-colors hover:text-foreground" aria-label="Back to conversations"><HiArrowLeft className="h-4 w-4" /></button>;
    $[15] = isMobile;
    $[16] = setActiveConversation;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== activeConversation || $[19] !== currentUser?.id || $[20] !== dmAvatarMember || $[21] !== onlineStatusMap) {
    t11 = activeConversation?.type === "DM" && dmAvatarMember ? <PresenceIndicator status={onlineStatusMap.has(dmAvatarMember.providerId) ? onlineStatusMap.get(dmAvatarMember.providerId) : undefined} size={32}><ProviderAvatar providerId={dmAvatarMember.providerId} size={32} /></PresenceIndicator> : activeConversation && <div className="relative h-8 w-8 shrink-0">{activeConversation.members.filter(m_0 => m_0.providerId !== currentUser?.id).slice(0, 2).map(_temp)}</div>;
    $[18] = activeConversation;
    $[19] = currentUser?.id;
    $[20] = dmAvatarMember;
    $[21] = onlineStatusMap;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== activeConversation?.type || $[24] !== conversationTitle || $[25] !== editName || $[26] !== isEditingName || $[27] !== updateConversation) {
    t12 = isEditingName ? <input ref={nameInputRef} value={editName} onChange={e => setEditName(e.target.value)} onBlur={() => {
      const trimmed = editName.trim();
      if (trimmed !== conversationTitle) {
        updateConversation.mutate({
          name: trimmed
        });
      }
      setIsEditingName(false);
    }} onKeyDown={e_0 => {
      if (e_0.key === "Enter") {
        (e_0.target as HTMLInputElement).blur();
      }
      if (e_0.key === "Escape") {
        setIsEditingName(false);
      }
    }} className="w-full truncate rounded border border-border bg-background px-1 py-0.5 text-body-emphasis text-foreground outline-none focus:ring-1 focus:ring-primary" /> : <h3 className={cx("truncate text-body-emphasis text-foreground", activeConversation?.type === "GROUP" && "cursor-pointer rounded px-1 py-0.5 hover:bg-hover")} onClick={() => {
      if (activeConversation?.type === "GROUP") {
        setEditName(conversationTitle);
        setIsEditingName(true);
        setTimeout(() => nameInputRef.current?.select(), 0);
      }
    }}>{conversationTitle}</h3>;
    $[23] = activeConversation?.type;
    $[24] = conversationTitle;
    $[25] = editName;
    $[26] = isEditingName;
    $[27] = updateConversation;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== activeConversation?.type || $[30] !== dmAvatarMember || $[31] !== presenceMap) {
    t13 = activeConversation?.type === "DM" && dmAvatarMember && presenceMap.get(dmAvatarMember.providerId)?.customStatusText && <p className="truncate text-caption text-foreground-muted">{presenceMap.get(dmAvatarMember.providerId)?.customStatusEmoji && <span className="mr-0.5">{presenceMap.get(dmAvatarMember.providerId)?.customStatusEmoji}</span>}{presenceMap.get(dmAvatarMember.providerId)?.customStatusText}</p>;
    $[29] = activeConversation?.type;
    $[30] = dmAvatarMember;
    $[31] = presenceMap;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12 || $[34] !== t13) {
    t14 = <div className="min-w-0 flex-1">{t12}{t13}</div>;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== activeConversation || $[37] !== isSelfDm) {
    t15 = activeConversation && !isSelfDm && <AddMemberPopover conversation={activeConversation} />;
    $[36] = activeConversation;
    $[37] = isSelfDm;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setIsSearchOpen(_temp2);
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const t17 = isSearchOpen ? "bg-hover text-foreground" : "text-foreground-muted hover:text-foreground";
  let t18;
  if ($[40] !== t17) {
    t18 = cx("shrink-0 rounded p-1.5 transition-colors", t17);
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <RiSearchLine className="h-4 w-4" />;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t18) {
    t20 = <button onClick={t16} className={t18}>{t19}</button>;
    $[43] = t18;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== activeConversation?.type || $[46] !== activeConversationId || $[47] !== leaveConversation || $[48] !== setActiveConversation) {
    t21 = activeConversation?.type === "GROUP" && <DropdownMenu><DropdownMenuTrigger asChild={true}><button className="shrink-0 rounded p-1.5 text-foreground-muted transition-colors hover:text-foreground"><RiMore2Fill className="h-4 w-4" /></button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem onClick={() => {
          if (activeConversationId) {
            leaveConversation.mutate(activeConversationId);
            setActiveConversation(null);
          }
        }} className="text-status-error-text"><RiLogoutBoxLine className="mr-2 h-4 w-4" />Leave Group</DropdownMenuItem></DropdownMenuContent></DropdownMenu>;
    $[45] = activeConversation?.type;
    $[46] = activeConversationId;
    $[47] = leaveConversation;
    $[48] = setActiveConversation;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== handleClose || $[51] !== isMobile) {
    t22 = isMobile && <Button variant="ghost" className="size-7 !shadow-none text-foreground-muted hover:text-foreground" onClick={handleClose} icon={RiCloseLine} tooltip="Close Whisper" aria-label="Close Whisper" />;
    $[50] = handleClose;
    $[51] = isMobile;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t10 || $[54] !== t11 || $[55] !== t14 || $[56] !== t15 || $[57] !== t20 || $[58] !== t21 || $[59] !== t22) {
    t23 = <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border px-4">{t10}{t11}{t14}{t15}{t20}{t21}{t22}</div>;
    $[53] = t10;
    $[54] = t11;
    $[55] = t14;
    $[56] = t15;
    $[57] = t20;
    $[58] = t21;
    $[59] = t22;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  const conversationDetailHeader = t23;
  if (isMobile) {
    let t24;
    if ($[61] !== className) {
      t24 = cx("flex h-full flex-col", className);
      $[61] = className;
      $[62] = t24;
    } else {
      t24 = $[62];
    }
    let t25;
    if ($[63] !== activeConversationId || $[64] !== conversationDetailHeader || $[65] !== isSearchOpen || $[66] !== listHeader || $[67] !== showMobileDetail) {
      t25 = showMobileDetail ? <>{conversationDetailHeader}<ConversationDetail key={activeConversationId} conversationId={activeConversationId} isSearchOpen={isSearchOpen} onSearchClose={() => setIsSearchOpen(false)} /></> : <>{listHeader}<div className="flex-1 overflow-hidden"><ConversationList activeId={activeConversationId} /></div></>;
      $[63] = activeConversationId;
      $[64] = conversationDetailHeader;
      $[65] = isSearchOpen;
      $[66] = listHeader;
      $[67] = showMobileDetail;
      $[68] = t25;
    } else {
      t25 = $[68];
    }
    let t26;
    if ($[69] !== t24 || $[70] !== t25) {
      t26 = <div className={t24}>{t25}</div>;
      $[69] = t24;
      $[70] = t25;
      $[71] = t26;
    } else {
      t26 = $[71];
    }
    return t26;
  }
  let t24;
  if ($[72] !== className) {
    t24 = cx("flex h-full", className);
    $[72] = className;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== activeConversationId) {
    t25 = <div className="flex-1 overflow-hidden"><ConversationList activeId={activeConversationId} /></div>;
    $[74] = activeConversationId;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  let t26;
  if ($[76] !== listHeader || $[77] !== t25) {
    t26 = <div className="flex w-64 shrink-0 flex-col border-r border-border">{listHeader}{t25}</div>;
    $[76] = listHeader;
    $[77] = t25;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== activeConversationId || $[80] !== conversationDetailHeader || $[81] !== isSearchOpen) {
    t27 = <div className="flex min-w-0 flex-1 flex-col">{activeConversationId ? <>{conversationDetailHeader}<ConversationDetail key={activeConversationId} conversationId={activeConversationId} isSearchOpen={isSearchOpen} onSearchClose={() => setIsSearchOpen(false)} /></> : <div className="flex flex-1 items-center justify-center text-body text-foreground-muted">Select a conversation to start chatting</div>}</div>;
    $[79] = activeConversationId;
    $[80] = conversationDetailHeader;
    $[81] = isSearchOpen;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== t24 || $[84] !== t26 || $[85] !== t27) {
    t28 = <div className={t24}>{t26}{t27}</div>;
    $[83] = t24;
    $[84] = t26;
    $[85] = t27;
    $[86] = t28;
  } else {
    t28 = $[86];
  }
  return t28;
}
function _temp2(v) {
  return !v;
}
function _temp(member, i) {
  return <div key={member.providerId} className={cx("absolute rounded-full ring-2 ring-background", i === 0 ? "bottom-0 right-0 z-[1]" : "left-0 top-0")}><ProviderAvatar providerId={member.providerId} size={20} /></div>;
}